import React, { createContext, useContext, useState, useCallback, useRef, useEffect } from 'react';
import CombosService from '../services/CombosService';
import ImageCacheAPIService from '../services/ImageCacheAPIService';
import { useAppContext } from './AppContext';

const CombosContext = createContext();

export const useCombos = () => useContext(CombosContext);

export const CombosProvider = ({ children }) => {
    const { sessionData } = useAppContext();
    // { [productId]: { loaded: bool, has_combos: bool, combo_groups: [...] } }
    const comboGroupsCache = useRef({});
    const [cacheReady, setCacheReady] = useState(false);

    useEffect(() => {
        if (sessionData?.products?.length) {
            comboGroupsCache.current = {};
            setCacheReady(false);

            const totemProducts = sessionData.products.filter(p => p.is_totem);

            const BATCH_SIZE = 5;
            const processProduct = async (product) => {
                try {
                    const result = await CombosService.getProductComboGroups(product.id, true);
                    comboGroupsCache.current[product.id] = {
                        loaded: true,
                        has_combos: result.has_combos,
                        combo_groups: result.combo_groups || []
                    };
                } catch {
                    comboGroupsCache.current[product.id] = {
                        loaded: true,
                        has_combos: false,
                        combo_groups: []
                    };
                }
            };

            const runBatched = async () => {
                for (let i = 0; i < totemProducts.length; i += BATCH_SIZE) {
                    const batch = totemProducts.slice(i, i + BATCH_SIZE);
                    await Promise.all(batch.map(processProduct));
                }
            };

            runBatched().then(() => {
                setCacheReady(true);

                // Cachear imágenes de productos bebida en el proxy local para acceso offline
                ImageCacheAPIService.cacheDrinkImages(comboGroupsCache.current)
                    .catch(() => {});
            });
        }
    }, [sessionData]);

    // Async fetch + cache on demand (fallback if preload missed a product)
    const getComboGroups = useCallback(async (productId) => {
        const cached = comboGroupsCache.current[productId];
        if (cached?.loaded) {
            console.log(`[Combos] producto ${productId} desde caché → has_combos=${cached.has_combos} grupos=${cached.combo_groups.length}`, cached.combo_groups.map(g => g.drink_group?.name));
            return { has_combos: cached.has_combos, combo_groups: cached.combo_groups };
        }
        try {
            const result = await CombosService.getProductComboGroups(productId, true);
            console.log(`[Combos] producto ${productId} desde API → has_combos=${result.has_combos} grupos=${(result.combo_groups||[]).length}`, (result.combo_groups||[]).map(g => g.drink_group?.name));
            comboGroupsCache.current[productId] = {
                loaded: true,
                has_combos: result.has_combos,
                combo_groups: result.combo_groups || []
            };
            return { has_combos: result.has_combos, combo_groups: result.combo_groups || [] };
        } catch {
            return { has_combos: false, combo_groups: [] };
        }
    }, []);

    // Synchronous read from cache — returns null if not yet loaded
    const hasComboGroupsSync = useCallback((productId) => {
        const cached = comboGroupsCache.current[productId];
        if (!cached?.loaded) return null;
        return cached.has_combos;
    }, []);

    const value = {
        getComboGroups,
        hasComboGroupsSync,
        cacheReady
    };

    return (
        <CombosContext.Provider value={value}>
            {children}
        </CombosContext.Provider>
    );
};

export default CombosContext;
